import React, { useState, useEffect } from 'react';
import { formatCurrencyWhole as formatCurrency } from '../lib/format';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Users,
  ShoppingCart,
  DollarSign,
  TrendingUp,
  Package,
  Building2,
  Shield,
  UserCheck,
  Activity,
  RefreshCw,
  Clock,
  BarChart2,
  ArrowLeft,
  ChevronRight,
  Calendar,
  Trophy,
  HeartPulse
} from 'lucide-react';
import { apiClient } from '../lib/api';
import { AsyncSearchSelect } from '../components/common/AsyncSearchSelect';
import type { ServerListQuery } from '../hooks/useServerList';
import { useAuthStore } from '../store/useAuthStore';
import DateRangePicker, { DateRange } from '../components/forms/DateRangePicker';
import { RevenueBreakdownModal } from '../components/analytics/RevenueBreakdownModal';
import { PhasedMetricsPanel } from '../components/analytics/PhasedMetricsPanel';
import type { PhaseMetricConfig, PhaseValues } from '../components/analytics/PhasedMetricsPanel';
import { ReorderRankingTable } from '../components/analytics/ReorderRankingTable';
import AccountManagerPerformancePanel from '../components/analytics/AccountManagerPerformancePanel';
import AccountHealthPanel from '../components/analytics/AccountHealthPanel';
import SalesRepLeaderboard from '../components/analytics/SalesRepLeaderboard';
import { BarChart } from '../components/charts/BarChart';
import { LineChart } from '../components/charts/LineChart';
import { PieChart as PieChartComponent } from '../components/charts/PieChart';
import { formatMonthString, formatShortDate, formatDate } from '../utils/dateUtils';

// AM picker option. Pickers search /users/distributors on the server (scoped
// to a supervisor's own AMs there): there are more AMs than one page holds.
type AmOption = { _id: string; profile?: { firstName?: string; lastName?: string }; distributorData?: { companyName?: string }; email?: string };
const amLabel = (am: AmOption) =>
  am.distributorData?.companyName || `${am.profile?.firstName || ''} ${am.profile?.lastName || ''}`.trim() || am._id;
const fetchAmPage = ({ page, pageSize, search, signal }: ServerListQuery<Record<string, never>>) =>
  apiClient.searchUsersPage({ role: 'distributor', page, pageSize, search, signal });

// Helper function for status colors
const getStatusColor = (status: string) => {
  const colors: { [key: string]: string } = {
    draft: '#9CA3AF',
    pending: '#F59E0B',
    submitted: '#3B82F6',
    processing: '#8B5CF6',
    shipped: '#10B981',
    delivered: '#059669',
    cancelled: '#EF4444',
    failed: '#DC2626'
  };
  return colors[status] || '#6B7280';
};

interface PhysicianPhasedAnalytics {
  scope: 'physician';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    physicianMargin: PhaseValues;
    clinicCost: PhaseValues;
  };
}

interface DistributorPhasedAnalytics {
  scope: 'distributor';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    // AM's cost of goods (pharmacy + admin + supervisor combined).
    productCost: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

interface SupervisorPhasedAnalytics {
  scope: 'supervisor';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    productCost: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

interface AdminPhasedAnalytics {
  scope: 'admin';
  summary: {
    totalRevenue: PhaseValues;
    thirtyDayRevenue?: PhaseValues;
    pharmacyCost: PhaseValues;
    adminMargin: PhaseValues;
    supervisorMargin: PhaseValues;
    distributorMargin: PhaseValues;
    physicianMargin: PhaseValues;
  };
}

type AnyPhasedAnalytics =
  | PhysicianPhasedAnalytics
  | DistributorPhasedAnalytics
  | SupervisorPhasedAnalytics
  | AdminPhasedAnalytics;

const getRollingThirtyDayRange = (): DateRange => {
  const endDate = new Date();
  endDate.setHours(23, 59, 59, 999);
  const startDate = new Date();
  startDate.setDate(startDate.getDate() - 30);
  startDate.setHours(0, 0, 0, 0);
  return { startDate, endDate };
};

const mergeThirtyDayRevenue = <T extends { summary: Record<string, PhaseValues | undefined> }>(
  phasedAnalytics: T,
  rollingThirtyDayRevenue?: PhaseValues
): T => ({
  ...phasedAnalytics,
  summary: {
    ...phasedAnalytics.summary,
    thirtyDayRevenue: rollingThirtyDayRevenue ?? phasedAnalytics.summary.totalRevenue
  }
});

const physicianPhaseMetricsBase: PhaseMetricConfig[] = [
  {
    key: 'totalRevenue',
    label: 'Total Cost',
    description: 'Paid, invoiced, and fully placed order cost.',
    accentClass: 'bg-yellow-500',
    valueClass: 'text-gray-900',
    hoverBorderClass: 'hover:bg-yellow-100'
  },
  {
    key: 'thirtyDayRevenue',
    label: '30D Cost',
    description: 'Rolling last 30 days of phased cost.',
    accentClass: 'bg-blue-500',
    valueClass: 'text-gray-900',
    hoverBorderClass: 'hover:bg-blue-100'
  }
];

// Extra metrics shown only for clinics that apply their own markup.
const physicianClinicMarkupMetrics: PhaseMetricConfig[] = [
  {
    key: 'clinicCost',
    label: 'Upstream Cost',
    description: 'Paid to pharmacy + upstream tiers.',
    accentClass: 'bg-gray-500',
    valueClass: 'text-gray-900',
    hoverBorderClass: 'hover:bg-gray-100'
  },
  {
    key: 'physicianMargin',
    label: 'Clinic Margin',
    description: "Your clinic's markup retained.",
    accentClass: 'bg-green-500',
    valueClass: 'text-green-900',
    hoverBorderClass: 'hover:bg-green-100'
  }
];

// True iff the physician's clinic actually applied markup in the window —
// used to decide whether to show clinicCost / physicianMargin.
const hasClinicMarkup = (phased: PhysicianPhasedAnalytics | null): boolean => {
  if (!phased?.summary.physicianMargin) return false;
  const m = phased.summary.physicianMargin;
  return (m.realized || 0) + (m.booked || 0) + (m.pipeline || 0) > 0;
};

const distributorPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'productCost',
    label: 'Your Cost',
    description: 'Your cost of goods on these orders',
    accentClass: 'bg-gray-500',
    titleClass: 'text-gray-900',
    valueClass: 'text-gray-900',
    descriptionClass: 'text-gray-600',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'distributorMargin',
    label: 'Your Account Manager Margin',
    description: 'Your commission on orders',
    accentClass: 'bg-red-500',
    titleClass: 'text-red-900',
    valueClass: 'text-red-900',
    descriptionClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Network Revenue',
    description: 'What physicians paid for orders',
    accentClass: 'bg-green-500',
    titleClass: 'text-green-900',
    valueClass: 'text-green-900',
    descriptionClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  },
  {
    key: 'thirtyDayRevenue',
    label: '30D Revenue',
    description: 'Rolling last 30 days of phased revenue',
    accentClass: 'bg-yellow-500',
    titleClass: 'text-yellow-900',
    valueClass: 'text-yellow-900',
    descriptionClass: 'text-yellow-600',
    hoverBorderClass: 'hover:border-yellow-300'
  }
];

const supervisorPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'productCost',
    label: 'Product Cost',
    description: 'Base cost before AM margins',
    accentClass: 'bg-gray-500',
    titleClass: 'text-gray-900',
    valueClass: 'text-gray-900',
    descriptionClass: 'text-gray-600',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'distributorMargin',
    label: 'Account Manager Margins',
    description: 'Commission earned by AMs in your network.',
    accentClass: 'bg-red-500',
    titleClass: 'text-red-900',
    valueClass: 'text-red-900',
    descriptionClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Network Revenue',
    description: 'What physicians paid for orders',
    accentClass: 'bg-green-500',
    titleClass: 'text-green-900',
    valueClass: 'text-green-900',
    descriptionClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  },
  {
    key: 'thirtyDayRevenue',
    label: '30D Revenue',
    description: 'Rolling last 30 days of phased revenue',
    accentClass: 'bg-yellow-500',
    titleClass: 'text-yellow-900',
    valueClass: 'text-yellow-900',
    descriptionClass: 'text-yellow-600',
    hoverBorderClass: 'hover:border-yellow-300'
  }
];

const adminPhaseMetrics: PhaseMetricConfig[] = [
  {
    key: 'pharmacyCost',
    label: 'Pharmacy Cost',
    description: 'Base cost flowing to the pharmacy network.',
    accentClass: 'text-gray-600',
    icon: Package,
    iconBgClass: 'bg-gray-100',
    valueClass: 'text-gray-700',
    hoverBorderClass: 'hover:border-gray-400'
  },
  {
    key: 'adminMargin',
    label: 'Admin Margin',
    description: 'Platform-retained margin.',
    accentClass: 'text-green-600',
    icon: DollarSign,
    iconBgClass: 'bg-green-100',
    valueClass: 'text-green-600',
    hoverBorderClass: 'hover:border-green-300'
  },
  {
    key: 'supervisorMargin',
    label: 'Supervisor Margin',
    description: 'Supervisor payout layer.',
    accentClass: 'text-orange-600',
    icon: Shield,
    iconBgClass: 'bg-orange-100',
    valueClass: 'text-orange-600',
    hoverBorderClass: 'hover:border-orange-300'
  },
  {
    key: 'distributorMargin',
    label: 'Account Manager Margin',
    description: 'Account manager payout layer.',
    accentClass: 'text-red-600',
    icon: Building2,
    iconBgClass: 'bg-red-100',
    valueClass: 'text-red-600',
    hoverBorderClass: 'hover:border-red-300'
  },
  {
    key: 'totalRevenue',
    label: 'Total Revenue',
    description: 'Top-line order value across all phases.',
    accentClass: 'text-blue-600',
    icon: TrendingUp,
    iconBgClass: 'bg-blue-100',
    valueClass: 'text-blue-600',
    hoverBorderClass: 'hover:border-blue-300'
  },
  {
    key: 'thirtyDayRevenue',
    label: '30D Revenue',
    description: 'Rolling last 30 days of phased revenue.',
    accentClass: 'text-yellow-600',
    icon: DollarSign,
    iconBgClass: 'bg-yellow-100',
    valueClass: 'text-yellow-600',
    hoverBorderClass: 'hover:border-yellow-300'
  }
];

// Reuse interfaces from Dashboard
interface PhysicianAnalytics {
  totalPatients: number;
  newPatientsThisMonth: number;
  activePatients: number;
  totalOrders: number;
  monthlyOrders: number;
  revenueThisMonth: number;
  totalRevenue: number;
  averageOrderValue: number;
  ordersByStatus: {
    draft: number;
    pending: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    cancelled: number;
    failed: number;
  };
  monthlyTrends: {
    month: string;
    orders: number;
    revenue: number;
    patients: number;
  }[];
  topProducts: {
    productName: string;
    orderCount: number;
    revenue: number;
  }[];
}

interface DistributorAnalytics {
  networkPhysicians: number;
  activePhysicians: number;
  totalPatients: number;
  totalOrders: number;
  monthlyOrders: number;
  networkRevenue: number;
  monthlyRevenue: number;
  averageOrderValue: number;
  revenueBreakdown: {
    // Legacy fields
    pharmacyRevenue: number;
    distributorRevenue: number;
    physicianRevenue: number;
    monthlyPharmacyRevenue: number;
    monthlyDistributorRevenue: number;
    monthlyPhysicianRevenue: number;
    // New explicit breakdown fields (paid/collected)
    pharmacyCost: number;
    adminMargin: number;
    supervisorMargin?: number;
    distributorMargin: number;
    physicianMargin: number;
    totalRevenue: number;
    monthlyPharmacyCost: number;
    monthlyAdminMargin: number;
    monthlySupervisorMargin?: number;
    monthlyDistributorMargin: number;
    monthlyPhysicianMargin: number;
    monthlyTotalRevenue: number;
    // Placed revenue (orders created)
    placedPharmacyCost?: number;
    placedAdminMargin?: number;
    placedDistributorMargin?: number;
    placedPhysicianMargin?: number;
    placedTotalRevenue?: number;
    // Outstanding = placed - paid
    outstandingRevenue?: number;
  };
  topPhysicians: {
    physicianId: string;
    physicianName: string;
    orderCount: number;
    revenue: number;
    patientCount: number;
  }[];
  monthlyTrends: {
    month: string;
    orders: number;
    revenue: number;
    physicians: number;
  }[];
  ordersByStatus: {
    draft: number;
    pending: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    cancelled: number;
    failed: number;
  };
}

interface AdminAnalytics {
  totalUsers: number;
  activeUsers: number;
  pendingUsers: number;
  totalDistributors: number;
  totalPhysicians: number;
  totalPatients: number;
  totalOrders: number;
  monthlyOrders: number;
  totalRevenue: number;
  monthlyRevenue: number;
  averageOrderValue: number;
  revenueBreakdown: {
    // Legacy fields
    pharmacyRevenue: number;
    distributorRevenue: number;
    physicianRevenue: number;
    monthlyPharmacyRevenue: number;
    monthlyDistributorRevenue: number;
    monthlyPhysicianRevenue: number;
    // New explicit breakdown fields (paid/collected)
    pharmacyCost: number;
    adminMargin: number;
    supervisorMargin: number;
    distributorMargin: number;
    physicianMargin: number;
    totalRevenue: number;
    monthlyPharmacyCost: number;
    monthlyAdminMargin: number;
    monthlySupervisorMargin: number;
    monthlyDistributorMargin: number;
    monthlyPhysicianMargin: number;
    monthlyTotalRevenue: number;
    // Placed revenue (orders created)
    placedPharmacyCost?: number;
    placedAdminMargin?: number;
    placedSupervisorMargin?: number;
    placedDistributorMargin?: number;
    placedPhysicianMargin?: number;
    placedTotalRevenue?: number;
    // Outstanding = placed - paid
    outstandingRevenue?: number;
  };
  userGrowth: {
    month: string;
    users: number;
    physicians: number;
    distributors: number;
  }[];
  ordersByStatus: {
    draft: number;
    pending: number;
    submitted: number;
    processing: number;
    shipped: number;
    delivered: number;
    cancelled: number;
    failed: number;
  };
  revenueByMonth: {
    month: string;
    revenue: number;
    orders: number;
    // New dual-perspective fields
    revenuePlaced?: number;
    revenueCollected?: number;
    ordersPlaced?: number;
    ordersCollected?: number;
    expenses?: number;
  }[];
  topDistributors: {
    distributorId: string;
    distributorName: string;
    physicianCount: number;
    orderCount: number;
    revenue: number;
    lastOrderDate: string | null;
  }[];
  topPhysicians: {
    physicianId: string;
    physicianName: string;
    orderCount: number;
    revenue: number;
    lastOrderDate: string | null;
  }[];
  profitAndLoss: {
    grossRevenue: number;
    pharmacyCost: number;
    supervisorPayouts: number;
    distributorPayouts: number;
    physicianPayouts: number;
    totalPayouts: number;
    pendingPayouts: number;
    netProfit: number;
    profitMargin: number;
  };
}

// StatCard component (reused from Dashboard)
interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ComponentType<{ className?: string }>;
  trend?: {
    value: number;
    isPositive: boolean;
  };
  bgGradient: string;
  isLoading?: boolean;
  onClick?: () => void;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon: Icon, 
  trend, 
  bgGradient, 
  isLoading = false,
  onClick 
}) => {
  return (
    <div 
      className={`bg-white rounded-2xl shadow-sm border border-gray-200 p-6 transition-all ${
        onClick ? 'cursor-pointer hover:border-blue-300 hover:shadow-md' : ''
      }`}
      onClick={onClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
          {isLoading ? (
            <div className="animate-pulse">
              <div className="h-8 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-16"></div>
            </div>
          ) : (
            <>
              <p className="text-2xl font-bold text-gray-900 mb-1">
                {typeof value === 'number' ? value.toLocaleString() : value}
              </p>
              {trend && (
                <div className={`flex items-center text-sm ${trend.isPositive ? 'text-green-600' : 'text-red-600'}`}>
                  <TrendingUp className={`h-4 w-4 mr-1 ${trend.isPositive ? '' : 'rotate-180'}`} />
                  <span>{trend.value}% vs last period</span>
                </div>
              )}
            </>
          )}
        </div>
        <div className={`w-12 h-12 ${bgGradient} rounded-xl flex items-center justify-center flex-shrink-0`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </div>
  );
};

const Analytics: React.FC = () => {
  const { user } = useAuthStore();
  const [analytics, setAnalytics] = useState<PhysicianAnalytics | DistributorAnalytics | AdminAnalytics | null>(null);
  const [phasedAnalytics, setPhasedAnalytics] = useState<AnyPhasedAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  
  // Date range state - use start/end of day boundaries to avoid off-by-one issues
  const [dateRange, setDateRange] = useState<DateRange>(() => {
    const endDate = new Date();
    endDate.setHours(23, 59, 59, 999);
    const startDate = new Date();
    startDate.setDate(startDate.getDate() - 30);
    startDate.setHours(0, 0, 0, 0);
    return { startDate, endDate };
  });

  // Revenue breakdown modal state
  const [revenueModalOpen, setRevenueModalOpen] = useState(false);
  const [selectedRevenueType, setSelectedRevenueType] = useState<string>('');
  const [selectedRevenueTitle, setSelectedRevenueTitle] = useState<string>('');

  const handleRevenueClick = (revenueType: string, title: string) => {
    setSelectedRevenueType(revenueType);
    setSelectedRevenueTitle(title);
    setRevenueModalOpen(true);
  };

  const fetchAnalytics = async () => {
    if (!user) return;
    
    try {
      setError(null);
      let response;
      let phasedResponse;
      let rollingThirtyDayPhasedResponse;
      
      switch (user.role) {
        case 'physician':
        case 'physician_readonly':
          [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
            apiClient.getPhysicianAnalytics(undefined, dateRange),
            apiClient.getPhysicianPhasedAnalytics(user._id, dateRange),
            apiClient.getPhysicianPhasedAnalytics(user._id, getRollingThirtyDayRange())
          ]);
          break;
        case 'distributor':
          [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
            apiClient.getDistributorAnalytics(undefined, dateRange),
            apiClient.getDistributorPhasedAnalytics(user._id, dateRange),
            apiClient.getDistributorPhasedAnalytics(user._id, getRollingThirtyDayRange())
          ]);
          break;
        case 'supervisor':
          [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
            apiClient.getSupervisorAnalytics(dateRange),
            apiClient.getSupervisorPhasedAnalytics(dateRange),
            apiClient.getSupervisorPhasedAnalytics(getRollingThirtyDayRange())
          ]);
          break;
        case 'admin':
          [response, phasedResponse, rollingThirtyDayPhasedResponse] = await Promise.all([
            apiClient.getSystemAnalytics(dateRange),
            apiClient.getSystemPhasedAnalytics(dateRange),
            apiClient.getSystemPhasedAnalytics(getRollingThirtyDayRange())
          ]);
          break;
        default:
          throw new Error('Unknown user role');
      }
      
      setAnalytics(response.data);
      if (phasedResponse?.success && phasedResponse.data) {
        setPhasedAnalytics(
          mergeThirtyDayRevenue(
            phasedResponse.data,
            rollingThirtyDayPhasedResponse?.success && rollingThirtyDayPhasedResponse.data
              ? rollingThirtyDayPhasedResponse.data.summary.totalRevenue
              : undefined
          )
        );
      } else {
        setPhasedAnalytics(null);
      }
    } catch (err: any) {
      console.error('Analytics fetch error:', err);
      setError(err.response?.data?.error || 'Failed to load analytics data');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchAnalytics();
  };

  const handleDateRangeChange = (newRange: DateRange) => {
    setDateRange(newRange);
    setLoading(true);
  };

  useEffect(() => {
    if (user) {
      fetchAnalytics();
    }
  }, [user, dateRange]);

  if (loading && !analytics) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <div className="h-8 bg-gray-200 rounded w-48 mb-2"></div>
            <div className="h-4 bg-gray-200 rounded w-64"></div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <StatCard
              key={i}
              title=""
              value=""
              icon={Activity}
              bgGradient="bg-gradient-to-br from-gray-500 to-gray-600"
              isLoading={true}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <div className="text-red-600 text-lg font-semibold mb-2">Error Loading Analytics</div>
        <div className="text-gray-600 mb-4">{error}</div>
        <button
          onClick={handleRefresh}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-600">No analytics data available</div>
      </div>
    );
  }

  // Role-specific rendering
  const renderDashboard = () => {
    if (!user) return <div>No user data</div>;
    
    switch (user.role) {
      case 'admin':
        return (
          <AdminAnalyticsView
            analytics={analytics as AdminAnalytics}
            phasedAnalytics={phasedAnalytics as AdminPhasedAnalytics | null}
            onRevenueClick={handleRevenueClick}
          />
        );
      case 'distributor':
      case 'supervisor':
        return (
          <DistributorAnalyticsView
            analytics={analytics as DistributorAnalytics}
            phasedAnalytics={phasedAnalytics as DistributorPhasedAnalytics | SupervisorPhasedAnalytics | null}
            onRevenueClick={handleRevenueClick}
            userRole={user.role}
          />
        );
      case 'physician':
      case 'physician_readonly':
        return (
          <PhysicianAnalyticsView
            analytics={analytics as PhysicianAnalytics}
            phasedAnalytics={phasedAnalytics as PhysicianPhasedAnalytics | null}
            onRevenueClick={handleRevenueClick}
          />
        );
      default:
        return <div>Unknown user role</div>;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header with controls */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Analytics Dashboard</h1>
          <p className="text-gray-600">Detailed performance insights and trends</p>
        </div>
        <div className="flex items-center space-x-4">
          <DateRangePicker
            value={dateRange}
            onChange={handleDateRangeChange}
          />
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Role-specific content */}
      {renderDashboard()}

      {/* Revenue Breakdown Modal */}
      <RevenueBreakdownModal
        isOpen={revenueModalOpen}
        onClose={() => setRevenueModalOpen(false)}
        revenueType={selectedRevenueType}
        title={selectedRevenueTitle}
        dateRange={dateRange}
      />
    </div>
  );
};

// Role-specific components
type AdminTab = 'overview' | 'clinic-opportunities' | 'am-performance' | 'sales-reps' | 'account-health';

const AdminAnalyticsView: React.FC<{
  analytics: AdminAnalytics;
  phasedAnalytics: AdminPhasedAnalytics | null;
  onRevenueClick: (revenueType: string, title: string) => void;
}> = ({ analytics, phasedAnalytics, onRevenueClick }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const tabParam = searchParams.get('tab') || 'overview';
  const initialDistributorId = searchParams.get('distributorId') || '';
  // Handle 'reorder-rankings' URL param (from distributor view) as 'clinic-opportunities'
  const initialTab: AdminTab =
    tabParam === 'reorder-rankings' || tabParam === 'clinic-opportunities' ? 'clinic-opportunities'
    : tabParam === 'am-performance' ? 'am-performance'
    : tabParam === 'sales-reps' ? 'sales-reps'
    : tabParam === 'account-health' ? 'account-health'
    : 'overview';
  const [activeTab, setActiveTab] = useState<AdminTab>(initialTab);
  const [selectedDistributorId, setSelectedDistributorId] = useState<string>(initialDistributorId);

  // Avg Patient Order Size — clinic-day composite rollup. See backend
  // analyticsService.getAvgPatientOrderSize for the rule.
  const [avgOrderSizeInterval, setAvgOrderSizeInterval] = useState<'day' | 'week' | 'month'>('day');
  const [avgOrderSize, setAvgOrderSize] = useState<{ bucket: string; avgOrderSize: number; compositeOrderCount: number; totalRevenue: number }[]>([]);
  useEffect(() => {
    let cancelled = false;
    apiClient.getAvgPatientOrderSize(avgOrderSizeInterval, 30).then(res => {
      if (cancelled) return;
      if (res.success && res.data) setAvgOrderSize(res.data.buckets);
    });
    return () => { cancelled = true; };
  }, [avgOrderSizeInterval]);

  const handleTabChange = (tab: AdminTab) => {
    setActiveTab(tab);
    const params: any = { tab };
    if ((tab === 'clinic-opportunities' || tab === 'am-performance') && selectedDistributorId) {
      params.distributorId = selectedDistributorId;
    }
    setSearchParams(params);
  };

  const handleDistributorChange = (distributorId: string) => {
    setSelectedDistributorId(distributorId);
    if (activeTab === 'clinic-opportunities' || activeTab === 'am-performance') {
      setSearchParams({ tab: activeTab, distributorId });
    }
  };

  // The AM picked in the picker. selectedDistributorId can also arrive from
  // the URL or the AM list below, so fall back to a stub labelled from analytics.
  const [pickedAm, setPickedAm] = useState<AmOption | null>(null);
  const selectedAmOption: AmOption | null = !selectedDistributorId ? null
    : pickedAm?._id === selectedDistributorId ? pickedAm
    : { _id: selectedDistributorId, distributorData: { companyName: analytics.topDistributors.find(d => d.distributorId === selectedDistributorId)?.distributorName || 'Selected account manager' } };


  const tabs = [
    { id: 'overview' as AdminTab, label: 'Overview', icon: BarChart2 },
    { id: 'sales-reps' as AdminTab, label: 'Sales Reps', icon: Trophy },
    { id: 'am-performance' as AdminTab, label: 'AM Performance', icon: Activity },
    { id: 'clinic-opportunities' as AdminTab, label: 'Clinic Opportunities', icon: TrendingUp },
    { id: 'account-health' as AdminTab, label: 'Account Health', icon: HeartPulse }
  ];

  // Prepare chart data
  const userGrowthData = analytics.userGrowth.map(growth => ({
    month: formatMonthString(growth.month),
    users: growth.users,
    physicians: growth.physicians,
    distributors: growth.distributors
  }));

  const revenueByMonthData = analytics.revenueByMonth.map(revenue => ({
    month: formatMonthString(revenue.month),
    revenue: revenue.revenue,
    orders: revenue.orders,
    // New dual-perspective fields (use legacy values as fallback)
    revenuePlaced: revenue.revenuePlaced ?? revenue.revenue,
    revenueCollected: revenue.revenueCollected ?? revenue.revenue,
    ordersPlaced: revenue.ordersPlaced ?? revenue.orders,
    ordersCollected: revenue.ordersCollected ?? revenue.orders
  }));

  const orderStatusData = Object.entries(analytics.ordersByStatus)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: getStatusColor(status)
    }));

  const topDistributorsData = analytics.topDistributors.slice(0, 10).map(distributor => ({
    name: distributor.distributorName.length > 20 
      ? distributor.distributorName.substring(0, 20) + '...' 
      : distributor.distributorName,
    physicians: distributor.physicianCount,
    orders: distributor.orderCount,
    revenue: distributor.revenue
  }));

  // Revenue breakdown for pie chart - 4 segments: Pharmacy Cost, Admin Margin, AM Margin, Physician Margin
  const revenueBreakdownData = [
    {
      name: 'Pharmacy Cost',
      value: analytics.revenueBreakdown.pharmacyCost,
      color: '#6B7280' // gray
    },
    {
      name: 'Admin Margin',
      value: analytics.revenueBreakdown.adminMargin,
      color: '#10B981' // green
    },
    {
      name: 'AM Margin',
      value: analytics.revenueBreakdown.distributorMargin,
      color: '#8B5CF6' // purple
    },
    {
      name: 'Physician Margin',
      value: analytics.revenueBreakdown.physicianMargin,
      color: '#3B82F6' // blue
    }
  ].filter(item => item.value > 0);

  const phasedMetricConfigs = adminPhaseMetrics.map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string }> = {
      pharmacyCost: { revenueType: 'pharmacy', title: 'Pharmacy Cost' },
      adminMargin: { revenueType: 'admin', title: 'Admin Margin' },
      supervisorMargin: { revenueType: 'supervisor', title: 'Supervisor Margin' },
      distributorMargin: { revenueType: 'distributor', title: 'Account Manager Margin' },
      totalRevenue: { revenueType: 'total', title: 'Total Revenue' }
    };

    const clickConfig = clickMap[metric.key];

    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => onRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Account Health Tab */}
      {activeTab === 'account-health' && (
        <AccountHealthPanel />
      )}

      {/* Sales Reps Tab */}
      {activeTab === 'sales-reps' && (
        <SalesRepLeaderboard />
      )}

      {/* AM Performance Tab */}
      {activeTab === 'am-performance' && (
        <div className="space-y-4">
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Account Manager:</label>
            <div className="w-72">
              <AsyncSearchSelect<AmOption>
                fetchPage={fetchAmPage}
                getKey={am => am._id}
                getLabel={amLabel}
                value={selectedAmOption}
                onChange={am => { setPickedAm(am); handleDistributorChange(am?._id ?? ''); }}
                placeholder="— pick an AM —"
                noun="account managers"
                aria-label="Account Manager"
              />
            </div>
          </div>
          <AccountManagerPerformancePanel
            amId={selectedDistributorId || null}
            cohortHint="admin"
          />
        </div>
      )}

      {/* Clinic Opportunities Tab */}
      {activeTab === 'clinic-opportunities' && (
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          {selectedDistributorId ? (
            // Drill-down view: Show clinic opportunities for selected AM
            <>
              <div className="mb-6">
                <button
                  onClick={() => handleDistributorChange('')}
                  className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 mb-3"
                >
                  <ArrowLeft className="w-4 h-4" />
                  Back to Account Managers
                </button>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {analytics.topDistributors.find(d => d.distributorId === selectedDistributorId)?.distributorName || 'Account Manager'}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Clinics who may need to order more
                  </p>
                </div>
              </div>
              <ReorderRankingTable
                showFilters={true}
                showPagination={true}
                distributorId={selectedDistributorId}
              />
            </>
          ) : (
            // List view: Show all AMs
            <>
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Account Manager Opportunities</h3>
                <p className="text-sm text-gray-500 mt-1">
                  Select an Account Manager to view their clinic opportunities
                </p>
              </div>
              <div className="data-table-container">
                <div className="data-table-scroll">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Account Manager</th>
                        <th className="text-right">Clinics</th>
                        <th className="text-right">Orders</th>
                        <th className="text-right">Revenue</th>
                        <th className="text-right">Last Order</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {analytics.topDistributors.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                            No Account Managers found
                          </td>
                        </tr>
                      ) : (
                        analytics.topDistributors.map((distributor) => (
                          <tr
                            key={distributor.distributorId}
                            onClick={() => handleDistributorChange(distributor.distributorId)}
                            className="cursor-pointer hover:bg-purple-50 transition-colors group"
                          >
                            <td className="cell-primary">
                              <div className="cell-inline cell-inline-icon">
                                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                                  <Building2 className="w-4 h-4 text-purple-600" />
                                </div>
                                <span>{distributor.distributorName}</span>
                              </div>
                            </td>
                            <td className="cell-secondary text-right">
                              <div className="cell-inline cell-inline-icon justify-end">
                                <Users className="w-3.5 h-3.5 text-gray-400" />
                                <span>{distributor.physicianCount}</span>
                              </div>
                            </td>
                            <td className="cell-secondary text-right">{distributor.orderCount}</td>
                            <td className="cell-mono text-right">
                              <div className="cell-inline cell-inline-icon justify-end">
                                <DollarSign className="w-3.5 h-3.5 text-gray-400" />
                                <span>
                                  {new Intl.NumberFormat('en-US', {
                                    style: 'currency',
                                    currency: 'USD',
                                    minimumFractionDigits: 0,
                                    maximumFractionDigits: 0
                                  }).format(distributor.revenue)}
                                </span>
                              </div>
                            </td>
                            <td className="cell-muted text-right">
                              <div className="cell-inline cell-inline-icon justify-end">
                                <Calendar className="w-3.5 h-3.5 text-gray-400" />
                                <span>
                                  {distributor.lastOrderDate
                                    ? formatShortDate(distributor.lastOrderDate)
                                    : 'Never'}
                                </span>
                              </div>
                            </td>
                            <td className="cell-actions text-center">
                              <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-purple-500 transition-colors inline-block" />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
      {/* System Overview Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Users"
          value={analytics.totalUsers}
          icon={Users}
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Physicians"
          value={analytics.totalPhysicians}
          icon={UserCheck}
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Account Managers"
          value={analytics.totalDistributors}
          icon={Building2}
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        <StatCard
          title="System Revenue"
          value={formatCurrency(analytics.totalRevenue)}
          icon={DollarSign}
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
        />
      </div>

      {/* System Health Overview */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">System Health Overview</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-6">
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Active Users</p>
                <p className="text-2xl font-bold text-blue-900">{analytics.activeUsers.toLocaleString()}</p>
                <p className="text-xs text-blue-600">
                  {analytics.totalUsers > 0 ? Math.round((analytics.activeUsers / analytics.totalUsers) * 100) : 0}% of total
                </p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pending Users</p>
                <p className="text-2xl font-bold text-yellow-900">{analytics.pendingUsers.toLocaleString()}</p>
                <p className="text-xs text-yellow-600">Awaiting approval</p>
              </div>
              <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Total Orders</p>
                <p className="text-2xl font-bold text-green-900">{analytics.totalOrders.toLocaleString()}</p>
                <p className="text-xs text-green-600">System-wide</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <ShoppingCart className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Avg Order Value</p>
                <p className="text-2xl font-bold text-purple-900">{formatCurrency(analytics.averageOrderValue)}</p>
                <p className="text-xs text-purple-600">Across all orders</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* User Growth Trends */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">User Growth Trends</h3>
          <LineChart
            data={userGrowthData}
            xKey="month"
            lines={[
              { dataKey: 'users', color: '#3B82F6', name: 'Total Users' },
              { dataKey: 'physicians', color: '#10B981', name: 'Physicians' },
              { dataKey: 'distributors', color: '#8B5CF6', name: 'Account Managers' }
            ]}
            height={300}
          />
        </div>

        {/* Revenue Trends - Placed vs Collected */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Trends</h3>
          <p className="text-xs text-gray-500 mb-4">Orders Placed (demand) vs Revenue Collected (payments received)</p>
          <LineChart
            data={revenueByMonthData}
            xKey="month"
            lines={[
              { dataKey: 'revenuePlaced', color: '#3B82F6', name: 'Orders Placed ($)' },
              { dataKey: 'revenueCollected', color: '#10B981', name: 'Revenue Collected ($)' }
            ]}
            height={300}
          />
        </div>

        {/* Avg Patient Order Size — same-clinic same-day patient orders are
            collapsed into one composite order before averaging. */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">Avg Order Size</h3>
            <div className="inline-flex rounded-md border border-gray-200 overflow-hidden text-xs">
              {(['day', 'week', 'month'] as const).map(opt => (
                <button
                  key={opt}
                  onClick={() => setAvgOrderSizeInterval(opt)}
                  className={`px-3 py-1.5 ${avgOrderSizeInterval === opt ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                >
                  {opt[0].toUpperCase() + opt.slice(1)}
                </button>
              ))}
            </div>
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Bulk orders count as one composite each; all patient orders from one clinic on the same day are summed into a single composite. Voided/cancelled excluded.
          </p>
          {avgOrderSize.length > 0 && avgOrderSize.some(b => b.compositeOrderCount > 0) ? (
            <LineChart
              data={avgOrderSize}
              xKey="bucket"
              lines={[
                { dataKey: 'avgOrderSize', name: 'Avg Order Size', color: '#0ea5e9', strokeWidth: 3 }
              ]}
              height={300}
              formatTooltip={(value: any, name: string) => {
                if (name === 'Avg Order Size') return [formatCurrency(value), name];
                return [value.toLocaleString(), name];
              }}
            />
          ) : (
            <div className="h-72 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <DollarSign className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No patient orders in window</p>
              </div>
            </div>
          )}
        </div>

        {/* Revenue Breakdown */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Revenue Distribution</h3>
          <PieChartComponent
            data={revenueBreakdownData}
            height={300}
            showLegend={true}
          />
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">System Order Status</h3>
          <PieChartComponent
            data={orderStatusData}
            height={300}
            showLegend={true}
          />
        </div>

        {/* Top Account Managers Performance */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6 xl:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Account Manager Performance</h3>
          <BarChart
            data={topDistributorsData}
            xKey="name"
            bars={[
              { dataKey: 'physicians', color: '#10B981', name: 'Physicians' },
              { dataKey: 'orders', color: '#3B82F6', name: 'Orders' }
            ]}
            height={300}
          />
        </div>
      </div>

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline layers for system economics."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="icon-cards"
          gridClassName="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6"
        />
      )}

      {/* Top Account Managers Table */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Account Manager Rankings</h3>
          <button
            onClick={() => navigate('/analytics/rankings/ams')}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View full ranking →
          </button>
        </div>

        <div className="data-table-container">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Account Manager</th>
                  <th>Physicians</th>
                  <th>Orders</th>
                  <th>Total Volume</th>
                  <th>Last Order</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topDistributors.map((distributor, index) => (
                  <tr key={distributor.distributorId}>
                    <td className="cell-secondary">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold
                        ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'}`}>
                        #{index + 1}
                      </div>
                    </td>
                    <td className="cell-primary">{distributor.distributorName}</td>
                    <td className="cell-secondary">{distributor.physicianCount.toLocaleString()}</td>
                    <td className="cell-secondary">{distributor.orderCount.toLocaleString()}</td>
                    <td className="cell-mono font-semibold">{formatCurrency(distributor.revenue)}</td>
                    <td className="cell-muted">
                      {distributor.lastOrderDate
                        ? formatDate(distributor.lastOrderDate)
                        : <span className="italic">No orders</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {analytics.topDistributors.length === 0 && (
          <div className="text-center py-8">
            <Building2 className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No account manager data</h3>
            <p className="mt-1 text-sm text-gray-500">
              No account managers have been added to the system yet.
            </p>
          </div>
        )}
      </div>

      {/* Top Physicians Table */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Top Physician Rankings</h3>
          <button
            onClick={() => navigate('/analytics/rankings/physicians')}
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            View full ranking →
          </button>
        </div>

        <div className="data-table-container">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Physician</th>
                  <th>Orders</th>
                  <th>Total Volume</th>
                  <th>Avg Order Value</th>
                  <th>Last Order</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topPhysicians.map((physician, index) => (
                  <tr key={physician.physicianId}>
                    <td className="cell-secondary">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold
                        ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'}`}>
                        #{index + 1}
                      </div>
                    </td>
                    <td className="cell-primary">{physician.physicianName}</td>
                    <td className="cell-secondary">{physician.orderCount.toLocaleString()}</td>
                    <td className="cell-mono font-semibold">{formatCurrency(physician.revenue)}</td>
                    <td className="cell-mono">
                      {formatCurrency(physician.orderCount > 0 ? physician.revenue / physician.orderCount : 0)}
                    </td>
                    <td className="cell-muted">
                      {physician.lastOrderDate
                        ? formatDate(physician.lastOrderDate)
                        : <span className="italic">No orders</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {analytics.topPhysicians.length === 0 && (
          <div className="text-center py-8">
            <UserCheck className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No physician data</h3>
            <p className="mt-1 text-sm text-gray-500">
              No physicians have placed orders yet.
            </p>
          </div>
        )}
      </div>

      {/* Profit & Loss Statement */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Profit & Loss Statement</h3>
        
        <div className="overflow-hidden">
          <table className="min-w-full">
            <tbody className="divide-y divide-gray-200">
              {/* Gross Revenue */}
              <tr className="bg-green-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="text-sm font-semibold text-green-900">Gross Revenue</span>
                  </div>
                  <p className="text-xs text-green-600 mt-1 ml-6">Total order revenue from all sales</p>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="text-lg font-bold text-green-900">
                    {formatCurrency(analytics.profitAndLoss?.grossRevenue || 0)}
                  </span>
                </td>
              </tr>
              
              {/* Cost of Goods (Pharmacy Cost) */}
              <tr className="bg-red-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                    <span className="text-sm font-semibold text-red-900">Product Cost (COGS)</span>
                  </div>
                  <p className="text-xs text-red-600 mt-1 ml-6">Base product cost paid to pharmacy</p>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="text-lg font-bold text-red-900">
                    -{formatCurrency(analytics.profitAndLoss?.pharmacyCost || 0)}
                  </span>
                </td>
              </tr>
              
              {/* Gross Margin Line */}
              <tr className="bg-gray-100">
                <td className="px-6 py-3">
                  <span className="text-sm font-medium text-gray-700 ml-6">Gross Margin</span>
                </td>
                <td className="px-6 py-3 text-right">
                  <span className="text-base font-semibold text-gray-700">
                    {formatCurrency((analytics.profitAndLoss?.grossRevenue || 0) - (analytics.profitAndLoss?.pharmacyCost || 0))}
                  </span>
                </td>
              </tr>
              
              {/* Supervisor Payouts (Completed) */}
              {(analytics.profitAndLoss?.supervisorPayouts || 0) > 0 && (
                <tr className="bg-orange-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                      <span className="text-sm font-semibold text-orange-900">Supervisor Payouts (Paid)</span>
                    </div>
                    <p className="text-xs text-orange-600 mt-1 ml-6">Commissions already paid to supervisors</p>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <span className="text-lg font-bold text-orange-900">
                      -{formatCurrency(analytics.profitAndLoss?.supervisorPayouts || 0)}
                    </span>
                  </td>
                </tr>
              )}

              {/* Distributor Payouts (Completed) */}
              <tr className="bg-purple-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-purple-500 rounded-full mr-3"></div>
                    <span className="text-sm font-semibold text-purple-900">Account Manager Payouts (Paid)</span>
                  </div>
                  <p className="text-xs text-purple-600 mt-1 ml-6">Commissions already paid to account managers</p>
                </td>
                <td className="px-6 py-4 text-right">
                  <span className="text-lg font-bold text-purple-900">
                    -{formatCurrency(analytics.profitAndLoss?.distributorPayouts || 0)}
                  </span>
                </td>
              </tr>
              
              {/* Pending Payouts (informational) */}
              {(analytics.profitAndLoss?.pendingPayouts || 0) > 0 && (
                <tr className="bg-yellow-50">
                  <td className="px-6 py-3">
                    <div className="flex items-center">
                      <div className="w-3 h-3 bg-yellow-500 rounded-full mr-3"></div>
                      <span className="text-sm font-medium text-yellow-900">Pending Payouts</span>
                    </div>
                    <p className="text-xs text-yellow-600 mt-1 ml-6">Approved but not yet sent (not deducted from profit)</p>
                  </td>
                  <td className="px-6 py-3 text-right">
                    <span className="text-base font-semibold text-yellow-700">
                      ({formatCurrency(analytics.profitAndLoss?.pendingPayouts || 0)})
                    </span>
                  </td>
                </tr>
              )}
              
              {/* Net Profit */}
              <tr className={`${(analytics.profitAndLoss?.netProfit || 0) >= 0 ? 'bg-blue-100' : 'bg-red-100'}`}>
                <td className="px-6 py-5">
                  <div className="flex items-center">
                    <div className={`w-4 h-4 ${(analytics.profitAndLoss?.netProfit || 0) >= 0 ? 'bg-blue-600' : 'bg-red-600'} rounded-full mr-3`}></div>
                    <span className={`text-base font-bold ${(analytics.profitAndLoss?.netProfit || 0) >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                      Net Profit
                    </span>
                  </div>
                  <p className={`text-xs mt-1 ml-7 ${(analytics.profitAndLoss?.netProfit || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                    After all costs and payouts
                  </p>
                </td>
                <td className="px-6 py-5 text-right">
                  <div>
                    <span className={`text-xl font-bold ${(analytics.profitAndLoss?.netProfit || 0) >= 0 ? 'text-blue-900' : 'text-red-900'}`}>
                      {formatCurrency(analytics.profitAndLoss?.netProfit || 0)}
                    </span>
                    <div className={`text-sm mt-1 ${(analytics.profitAndLoss?.profitMargin || 0) >= 0 ? 'text-blue-600' : 'text-red-600'}`}>
                      {(analytics.profitAndLoss?.profitMargin || 0).toFixed(1)}% margin
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}
    </div>
  );
};

type DistributorTab = 'overview' | 'reorder-rankings' | 'am-performance';

const DistributorAnalyticsView: React.FC<{
  analytics: DistributorAnalytics;
  phasedAnalytics: DistributorPhasedAnalytics | SupervisorPhasedAnalytics | null;
  onRevenueClick: (revenueType: string, title: string) => void;
  userRole?: string;
}> = ({ analytics, phasedAnalytics, onRevenueClick, userRole }) => {
  const isSupervisor = userRole === 'supervisor';
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DistributorTab) || 'overview';
  const [activeTab, setActiveTab] = useState<DistributorTab>(initialTab);

  const handleTabChange = (tab: DistributorTab) => {
    setActiveTab(tab);
    setSearchParams({ tab });
  };


  // Prepare chart data
  const monthlyTrendsData = analytics.monthlyTrends.map(trend => ({
    month: formatMonthString(trend.month),
    revenue: trend.revenue,
    orders: trend.orders,
    physicians: trend.physicians
  }));

  const orderStatusData = Object.entries(analytics.ordersByStatus)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: getStatusColor(status)
    }));

  const topPhysiciansData = analytics.topPhysicians.slice(0, 10).map(physician => ({
    name: physician.physicianName.length > 25
      ? physician.physicianName.substring(0, 25) + '...'
      : physician.physicianName,
    orders: physician.orderCount,
    revenue: physician.revenue,
    patients: physician.patientCount
  }));

  const tabs: { id: DistributorTab; label: string; icon: any }[] = [
    { id: 'overview', label: 'Overview', icon: BarChart2 },
    ...(isSupervisor ? [{ id: 'am-performance' as DistributorTab, label: 'AM Performance', icon: Activity }] : []),
    { id: 'reorder-rankings', label: 'Clinic Opportunities', icon: TrendingUp }
  ];

  // Supervisor AM picker (their own AMs only, scoped server-side).
  const [selectedAmForPerf, setSelectedAmForPerf] = useState<AmOption | null>(null);

  const phasedMetricConfigs = (isSupervisor ? supervisorPhaseMetrics : distributorPhaseMetrics).map(metric => {
    const clickMap: Record<string, { revenueType: string; title: string } | undefined> = isSupervisor
      ? {
          supervisorMargin: { revenueType: 'supervisor', title: 'Your Supervisor Margin' },
          distributorMargin: { revenueType: 'distributor', title: 'Account Manager Margins' }
        }
      : {
          distributorMargin: { revenueType: 'distributor', title: 'Your Account Manager Margin' }
        };

    const clickConfig = clickMap[metric.key];

    return {
      ...metric,
      clickable: Boolean(clickConfig),
      onClick: clickConfig
        ? () => onRevenueClick(clickConfig.revenueType, clickConfig.title)
        : undefined
    };
  });

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id)}
                className={`
                  flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                  ${activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}
                `}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* Clinic Opportunities Tab */}
      {activeTab === 'reorder-rankings' && (
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Clinic Opportunities</h3>
            <p className="text-sm text-gray-500 mt-1">
              Identify clinics who may need to order more. Rankings are weighted by spending history and time since last order.
            </p>
          </div>
          <ReorderRankingTable
            showFilters={true}
            showPagination={true}
          />
        </div>
      )}

      {/* AM Performance Tab (supervisor only) */}
      {isSupervisor && activeTab === 'am-performance' && (
        <div className="space-y-4">
          <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-4 flex flex-wrap items-center gap-3">
            <label className="text-sm font-medium text-gray-700">Account Manager:</label>
            <div className="w-72">
              <AsyncSearchSelect<AmOption>
                fetchPage={fetchAmPage}
                getKey={am => am._id}
                getLabel={amLabel}
                value={selectedAmForPerf}
                onChange={setSelectedAmForPerf}
                placeholder="— pick an AM —"
                noun="account managers"
                aria-label="Account Manager"
              />
            </div>
          </div>
          <AccountManagerPerformancePanel
            amId={selectedAmForPerf?._id || null}
            cohortHint="supervisor"
          />
        </div>
      )}

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <>
      {/* Key Metrics */}
      <div className={`grid grid-cols-1 md:grid-cols-2 ${isSupervisor ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6`}>
        <StatCard
          title="Network Physicians"
          value={analytics.networkPhysicians}
          icon={Users}
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Active Physicians"
          value={analytics.activePhysicians}
          icon={UserCheck}
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Network Orders"
          value={analytics.totalOrders}
          icon={ShoppingCart}
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
        {isSupervisor && (
          <StatCard
            title="Your Supervisor Margin"
            value={formatCurrency(analytics.revenueBreakdown.supervisorMargin || 0)}
            icon={DollarSign}
            bgGradient="bg-gradient-to-br from-orange-500 to-orange-600"
            onClick={() => onRevenueClick('supervisor', 'Your Supervisor Margin')}
          />
        )}
        <StatCard
          title={isSupervisor ? "Account Manager Margins" : "Your Account Manager Margin"}
          value={formatCurrency(analytics.revenueBreakdown.distributorMargin)}
          icon={DollarSign}
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
          onClick={() => onRevenueClick('distributor', isSupervisor ? 'Account Manager Margins' : 'Your Account Manager Margin')}
        />
      </div>

      {/* Network Performance Overview */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Network Performance Overview</h3>
        
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
          <div className="bg-blue-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Engagement Rate</p>
                <p className="text-2xl font-bold text-blue-900">
                  {analytics.networkPhysicians > 0 ? 
                    Math.round((analytics.activePhysicians / analytics.networkPhysicians) * 100) : 0}%
                </p>
                <p className="text-xs text-blue-600">{analytics.activePhysicians}/{analytics.networkPhysicians} active</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <Activity className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Avg Orders/Physician</p>
                <p className="text-2xl font-bold text-green-900">
                  {analytics.activePhysicians > 0 ? 
                    Math.round(analytics.totalOrders / analytics.activePhysicians) : 0}
                </p>
                <p className="text-xs text-green-600">Active physicians only</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>
          
          <div className="bg-purple-50 rounded-xl p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Network Revenue</p>
                <p className="text-2xl font-bold text-purple-900">{formatCurrency(analytics.networkRevenue)}</p>
                <p className="text-xs text-purple-600">Total network volume</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <DollarSign className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>
      </div>

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Economics View"
          subtitle="Realized, booked, and pipeline revenue layers."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="dot-cards"
          gridClassName="grid grid-cols-1 lg:grid-cols-4 gap-6"
        />
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Network Trends */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Network Growth Trends</h3>
          <LineChart
            data={monthlyTrendsData}
            xKey="month"
            lines={[
              { dataKey: 'revenue', color: '#8B5CF6', name: 'Network Revenue ($)' },
              { dataKey: 'orders', color: '#3B82F6', name: 'Network Orders' },
              { dataKey: 'physicians', color: '#10B981', name: 'Active Physicians' }
            ]}
            height={300}
          />
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Network Order Status</h3>
          <PieChartComponent
            data={orderStatusData}
            height={300}
            showLegend={true}
          />
        </div>

        {/* Top Physicians Performance */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6 xl:col-span-2">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Physician Performance</h3>
          <BarChart
            data={topPhysiciansData}
            xKey="name"
            bars={[
              { dataKey: 'orders', color: '#3B82F6', name: 'Orders' },
              { dataKey: 'patients', color: '#10B981', name: 'Patients' }
            ]}
            height={300}
          />
        </div>
      </div>

      {/* Detailed Physician Rankings */}
      <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-6">Physician Performance Rankings</h3>

        <div className="data-table-container">
          <div className="data-table-scroll">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Physician</th>
                  <th>Orders</th>
                  <th>Patients</th>
                  <th>Total Volume</th>
                  <th>Avg Order Value</th>
                </tr>
              </thead>
              <tbody>
                {analytics.topPhysicians.map((physician, index) => (
                  <tr key={physician.physicianId}>
                    <td className="cell-secondary">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold
                        ${index === 0 ? 'bg-yellow-500' : index === 1 ? 'bg-gray-400' : index === 2 ? 'bg-amber-600' : 'bg-gray-300'}`}>
                        #{index + 1}
                      </div>
                    </td>
                    <td className="cell-primary">{physician.physicianName}</td>
                    <td className="cell-secondary">{physician.orderCount.toLocaleString()}</td>
                    <td className="cell-secondary">{physician.patientCount.toLocaleString()}</td>
                    <td className="cell-mono font-semibold">{formatCurrency(physician.revenue)}</td>
                    <td className="cell-mono">
                      {formatCurrency(physician.orderCount > 0 ? physician.revenue / physician.orderCount : 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {analytics.topPhysicians.length === 0 && (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No physician data</h3>
            <p className="mt-1 text-sm text-gray-500">
              Physicians in your network haven't placed orders yet.
            </p>
          </div>
        )}
      </div>
        </>
      )}
    </div>
  );
};

const PhysicianAnalyticsView: React.FC<{ 
  analytics: PhysicianAnalytics; 
  phasedAnalytics: PhysicianPhasedAnalytics | null;
  onRevenueClick: (revenueType: string, title: string) => void; 
}> = ({ analytics, phasedAnalytics, onRevenueClick }) => {

  // Prepare chart data
  const monthlyTrendsData = analytics.monthlyTrends.map(trend => ({
    month: formatMonthString(trend.month),
    revenue: trend.revenue,
    orders: trend.orders,
    patients: trend.patients
  }));

  const orderStatusData = Object.entries(analytics.ordersByStatus)
    .filter(([_, count]) => count > 0)
    .map(([status, count]) => ({
      name: status.charAt(0).toUpperCase() + status.slice(1),
      value: count,
      color: getStatusColor(status)
    }));

  const topProductsData = analytics.topProducts.slice(0, 5).map(product => ({
    product: product.productName.length > 20 
      ? product.productName.substring(0, 20) + '...' 
      : product.productName,
    orders: product.orderCount,
    revenue: product.revenue
  }));

  const physicianMetricsForRender = hasClinicMarkup(phasedAnalytics)
    ? [...physicianPhaseMetricsBase, ...physicianClinicMarkupMetrics]
    : physicianPhaseMetricsBase;

  const phasedMetricConfigs = physicianMetricsForRender.map(metric => ({
    ...metric,
    clickable: metric.key === 'totalRevenue' || metric.key === 'physicianMargin',
    onClick:
      metric.key === 'totalRevenue'
        ? () => onRevenueClick('physician', 'Your Physician Revenue')
        : metric.key === 'physicianMargin'
          ? () => onRevenueClick('physician', 'Clinic Margin')
          : undefined
  }));

  return (
    <div className="space-y-6">
      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Total Patients"
          value={analytics.totalPatients}
          icon={Users}
          bgGradient="bg-gradient-to-br from-blue-500 to-blue-600"
        />
        <StatCard
          title="Total Orders"
          value={analytics.totalOrders}
          icon={ShoppingCart}
          bgGradient="bg-gradient-to-br from-green-500 to-green-600"
        />
        <StatCard
          title="Total Cost"
          value={formatCurrency(analytics.totalRevenue)}
          icon={DollarSign}
          bgGradient="bg-gradient-to-br from-yellow-500 to-yellow-600"
          onClick={() => onRevenueClick('physician', 'Your Physician Cost')}
        />
        <StatCard
          title="Avg Order Value"
          value={formatCurrency(analytics.averageOrderValue)}
          icon={TrendingUp}
          bgGradient="bg-gradient-to-br from-purple-500 to-purple-600"
        />
      </div>

      {phasedAnalytics && (
        <PhasedMetricsPanel
          title="Phased Revenue View"
          subtitle="Realized, booked, and pipeline revenue layers."
          metrics={phasedMetricConfigs}
          summary={phasedAnalytics.summary}
          variant="summary-list"
        />
      )}

      {/* Charts Grid */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Monthly Trends */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Practice Trends</h3>
          <LineChart
            data={monthlyTrendsData}
            xKey="month"
            lines={[
              { dataKey: 'revenue', color: '#10B981', name: 'Revenue ($)' },
              { dataKey: 'orders', color: '#3B82F6', name: 'Orders' },
              { dataKey: 'patients', color: '#8B5CF6', name: 'New Patients' }
            ]}
            height={300}
          />
        </div>

        {/* Order Status Distribution */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Order Status Distribution</h3>
          <PieChartComponent
            data={orderStatusData}
            height={300}
            showLegend={true}
          />
        </div>

        {/* Top Products */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Top Products by Orders</h3>
          <BarChart
            data={topProductsData}
            xKey="product"
            bars={[
              { dataKey: 'orders', color: '#3B82F6', name: 'Orders' }
            ]}
            height={300}
          />
        </div>

        {/* Practice Summary */}
        <div className="bg-white shadow-sm rounded-2xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Practice Summary</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Active Patients</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.activePatients.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                <span className="font-medium text-gray-700">New This Month</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.newPatientsThisMonth.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Monthly Orders</span>
              </div>
              <span className="font-semibold text-gray-900">{analytics.monthlyOrders.toLocaleString()}</span>
            </div>
            <div className="flex justify-between items-center p-4 bg-gray-50 rounded-xl">
              <div className="flex items-center space-x-3">
                <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                <span className="font-medium text-gray-700">Monthly Revenue</span>
              </div>
              <span className="font-semibold text-gray-900">{formatCurrency(analytics.revenueThisMonth)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Analytics;
